import React, { useEffect, useRef } from 'react';
import { useOvermindActions } from '#store/overmind';
import { ITeaserRenderer } from '#components/lists/horizontal-scroll-list/HorizontalScrollList';
import TeaserGridTeaserRenderer from '#components/lists/teaser-grid/TeaserGridTeaserRenderer';
import { getTeaserDetailsPosition } from '#utils';
import { TEASER_HOVER_DELAY } from '#config/constants';
import { useInputDeviceInfo } from '#hooks';
import { Teaser } from '#types/generated/graphql-types';

export type Props = {
  items?: Teaser[];
  TeaserComponent?: React.FC<ITeaserRenderer>;
  renderItem?: (item: Teaser, index: number) => JSX.Element;
};

const TeaserListRenderer = ({
  items = [],
  TeaserComponent = TeaserGridTeaserRenderer,
  renderItem,
}: Props) => {
  const { isTouchDevice } = useInputDeviceInfo();
  const { showDetails, hideDetails } = useOvermindActions().teaserDetails;
  const hoverDelay = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTeaserMouseEnter = (event: React.MouseEvent, itemData) => {
    if (isTouchDevice) return;

    const target = event.target as HTMLElement;

    const { centerX, centerY } = getTeaserDetailsPosition(target);

    if (hoverDelay.current) {
      clearTimeout(hoverDelay.current);
    }

    hoverDelay.current = setTimeout(() => {
      showDetails({
        data: itemData,
        position: { top: centerY, left: centerX },
      });
    }, TEASER_HOVER_DELAY);
  };

  const handleTeaserMouseLeave = () => {
    if (hoverDelay.current) {
      clearTimeout(hoverDelay.current);
      hoverDelay.current = null;
    }
  };

  const renderItemComponent = (item, index) => {
    if (renderItem) {
      return renderItem(item, index);
    }

    return (
      <TeaserComponent
        key={typeof item === 'string' ? item : item.id}
        teaserDataRaw={item}
        index={index}
        onMouseEnter={(event) => handleTeaserMouseEnter(event, item)}
        onMouseLeave={() => handleTeaserMouseLeave()}
      />
    );
  };

  useEffect(() => {
    hideDetails();
    return () => {
      hideDetails();
    };
  }, [items]);

  return (
    <>
      {items.map((item, index) => {
        return renderItemComponent(item, index);
      })}
    </>
  );
};

export default TeaserListRenderer;
